import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, useIsAdmin } from "@/lib/auth";

/* Canonical copy mirror of backend/pricing.py — used ONLY if /meta/pricing fails to load,
   so the pricing page never renders without plan cards. Keep in sync with the backend. */
export const FALLBACK_PRICING = {
  currency: "usd",
  offer_line: "Coaches are free — profile, matches, full job details and replies. Posting coaching jobs is free for every program. Program Recruiting — proactive talent search, ongoing matching and market intelligence — is $5/month per program, cancel anytime.",
  plans: [
    { key: "coach_free", audience: "coach", label: "Coach", price: "Free", features: [
      "Claim or create your profile — free, no card", "Full match details: program name, location and the fit rationale",
      "Express interest and reply to programs", "Optional weekly job alerts — you opt in, one email a week, skipped when nothing is new", "Privacy controls — anonymous to your current employer, hide from any program"] },
    { key: "program_basic", audience: "program", label: "Program Basic", price: "Free", features: [
      "Program account", "Unlimited job postings — HS, college and club", "Applicant & response management",
      "Reply to every coach who expresses interest", "Vetting insight on coaches who applied to you"] },
    { key: "program_recruiting", audience: "program", label: "Program Recruiting", price: "$5/month", per: "per program",
      package_id: "program_recruiting_monthly", features: [
      "Proactive talent search across the coach pool", "Ongoing matching for every open role", "Market intelligence & national program rank",
      "Invite coaches to claim their profile", "Stealth search preview + NDA-gated identity reveal"] },
  ],
  active_packages: [{ package_id: "program_recruiting_monthly", amount: 5.0 }],
  fallback: true,
};

/* Single front-end consumer of the canonical pricing API (/meta/pricing). */
export function usePricing() {
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/meta/pricing")
      .then(({ data }) => setPricing(data?.plans?.length ? data : FALLBACK_PRICING))
      .catch(() => setPricing(FALLBACK_PRICING));
  }, []);
  return pricing;
}

export function RecruitingCheckoutButton({ className = "", label }) {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const pricing = usePricing();
  const [busy, setBusy] = useState(false);
  const pkg = pricing?.active_packages?.[0];
  const price = pricing?.plans?.find((p) => p.key === "program_recruiting")?.price || "$5/month";
  if (!user) {
    return (
      <Link to="/auth?mode=signup&role=athletic_director" data-testid="recruiting-signup-link"
        className={`inline-flex items-center gap-2 bg-blaze text-white heading text-sm px-5 py-3 hover:glow-blaze transition-all ${className}`}>
        CREATE A FREE PROGRAM ACCOUNT →
      </Link>
    );
  }
  if (user.role !== "athletic_director") return null;
  const checkout = async () => {
    if (isAdmin) { toast.success("Admin · Program Recruiting already unlocked."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg?.package_id || "program_recruiting_monthly", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Checkout failed"));
      setBusy(false);
    }
  };
  return (
    <button onClick={checkout} disabled={busy} data-testid="checkout-program_recruiting_monthly-btn"
      className={`inline-flex items-center gap-2 bg-blaze text-white heading text-sm px-5 py-3 hover:glow-blaze transition-all disabled:opacity-50 ${className}`}>
      {busy ? "OPENING CHECKOUT…" : label || `START PROGRAM RECRUITING — ${price.toUpperCase()}`}
    </button>
  );
}

/* Shown in place of a Recruiting-only view when the API answers 402 recruiting_required. */
export function RecruitingGate({ feature = "This view" }) {
  const pricing = usePricing();
  const plan = pricing?.plans?.find((p) => p.key === "program_recruiting");
  return (
    <div className="border border-blaze bg-[#140A08] p-8 max-w-2xl" data-testid="recruiting-gate">
      <div className="text-xs tracking-widest text-blaze flex items-center gap-2"><Lock size={14} /> PROGRAM RECRUITING</div>
      <h2 className="heading text-3xl mt-2">{feature.toUpperCase()} IS PART OF PROGRAM RECRUITING.</h2>
      <p className="text-neutral-300 text-sm mt-3">
        {plan?.price || "$5/month"} per program, cancel anytime. Job posting, applicant management and replies stay free on Program Basic.
      </p>
      <ul className="mt-4 space-y-1">
        {(plan?.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-neutral-300"><Check className="text-blaze mt-0.5 shrink-0" size={14} />{f}</li>
        ))}
      </ul>
      <div className="mt-6 flex items-center gap-4">
        <RecruitingCheckoutButton />
        <Link to="/pricing" className="text-xs text-neutral-400 underline" data-testid="recruiting-gate-pricing-link">See all pricing</Link>
      </div>
    </div>
  );
}

export const isRecruitingRequired = (err) => err?.response?.status === 402 && err?.response?.data?.detail?.code === "recruiting_required";
